<template>
  <div class="booking" :class="darkMode ? 'dark' : ''">
    <div class="bookingcontainer container">
      <div class="subheader">
        <UIButton icon="trash"
          :tiny="true"
          class="cancelbooking"
          @click="cancelBooking()"
          v-if="booking.id">{{ $t('Cancel Reservation') }}</UIButton>
      </div>

      <ContentBlockGroup>
        <ContentBlock :loading="isLoading" :darkMode="darkMode">
          <div slot="instructions">
            <h5>{{ $t('Date and Time') }}</h5>
            <p>{{ $t('Select a date and time for the event.') }}</p>
          </div>
          <DateAndTime v-if="startTime"
            :booking="booking"
            :bookings="bookings"
            :location="location"
            :currentTime="currentTime"
            :startTime="startTime"
            :darkMode="darkMode"
            @setStartingTime="setStartingTime"
            @setStartingTimeNow="setStartingTimeNow"
            @edit:booking="editBooking" />
        </ContentBlock>
        <ContentBlock :loading="isLoading" :darkMode="darkMode">
          <div slot="instructions">
            <h5>{{ $t('Players') }}</h5>
          </div>
          <Players v-if="startTime"
            :booking="booking"
            :location="location"
            @edit:booking="editBooking" />
        </ContentBlock>
      </ContentBlockGroup>

      <div class="experiencesettings"
        v-for="(bookingStationTime, index) of booking.bookingStationTimes"
        :key="bookingStationTime._id">
        <ContentBlockGroup v-if="!bookingStationTime._removed">
          <ContentBlock :loading="isLoading" :darkMode="darkMode">
            <div class="topofstationbookingtime">
              <h2 class="f2">{{ $t('Station') }} {{ index + 1 }}</h2>
              <UIButton secondary
                :tiny="true"
                icon="trash"
                class="remove"
                @click="removeBookingStationTime(bookingStationTime._id)"
                v-if="bookingStationTimesWithoutRemoved.length > 1"
              >{{ $t('Delete Group') }}</UIButton>
              <Experience
                :stations="stations"
                :darkMode="darkMode"
                :bookingStationTime="bookingStationTime"
                :experiences="experiences"
                @experienceAction="experience => experienceAction(experience, bookingStationTime._id, booking)" />
            </div>
          </ContentBlock>
          <ContentBlock :loading="isLoading" :darkMode="darkMode">
            <Stations
              :startTime="startTime"
              :bookings="bookings"
              :booking="booking"
              :bookingStationTime="bookingStationTime"
              :stations="getExperienceStations(bookingStationTime.experience.id, bookingStationTime._id)"
              @stationAction="station => stationAction(station, bookingStationTime._id)" />
          </ContentBlock>
          <ContentBlock :loading="isLoading" :darkMode="darkMode">
            <Tier
              v-if="bookingStationTime.tier"
              :startTime="startTime"
              :location="location"
              :booking="booking"
              :bookings="bookings"
              :bookingStationTime="bookingStationTime"
              :tiers="getExperienceTiers(bookingStationTime.experience.id)"
              :isOrganizationEducation="isOrganizationEducation"
              @tierAction="tier => tierAction(tier, bookingStationTime._id)" />
          </ContentBlock>
          <ContentBlock :loading="isLoading" :darkMode="darkMode">
            <Promos
              v-if="!isOrganizationEducation"
              :locationId="location.id"
              :bookingStationTime="bookingStationTime"
              :coupons="getExperienceCoupons(bookingStationTime.experience.id)"
              :discounts="getExperienceDiscounts(bookingStationTime.experience.id)"
              @couponAction="coupon => couponAction(coupon, bookingStationTime._id)"
              @discountAction="discount => discountAction(discount, bookingStationTime._id)" />
          </ContentBlock>
        </ContentBlockGroup>

        <ContentBlockGroup v-if="bookingStationTime._removed">
          <ContentBlock :loading="isLoading" :darkMode="darkMode">
            <div class="deletedgroup">
              <p class="f5">{{ $t('The group was successfully deleted.') }}</p>
              <UIButton tiny class="undodeletegroup" @click="undoRemoveBookingStationTime(bookingStationTime._id)">{{ $t('Undo') }}</UIButton>
            </div>
          </ContentBlock>
        </ContentBlockGroup>
      </div>

      <div class="addstation"
        v-if="booking.bookingStationTimes.length < stations.length">
        <div class="line"></div>
        <UIButton :ghost="true"
          @click="addBookingStationTime()">{{ $t('Add Station') }}</UIButton>
        <div class="line"></div>
      </div>

      <ContentBlock :loading="isLoading" required :darkMode="darkMode">
        <div slot="instructions">
          <h3 class="f3">{{ $t('Host Information') }}</h3>
          <p class="f5">{{ $t("The host's reservation preferences and contact information.") }}</p>
        </div>
        <HostInformation v-if="booking.host"
          :booking="booking"
          :darkMode="darkMode"
          @edit:booking="editBooking"
        />
      </ContentBlock>

      <SummaryGrid :locationId="location.id"
        :twentyFourHourTime="location.twentyFourHourTime"
        :longestTierLength="longestTierLength"
        :booking="booking"
        :bookingStationTimesWithoutRemoved="bookingStationTimesWithoutRemoved"
        :startTime="startTime"
        :isOrganizationEducation="isOrganizationEducation"
        @toggleMarkAsPaid="toggleMarkAsPaid" />
    </div>

    <BottomOfBooking v-if="booking.bookingStationTimes"
      :locationId="location.id"
      :twentyFourHourTime="location.twentyFourHourTime"
      :longestTierLength="longestTierLength"
      :canProceedWithConflict="true"
      :booking="booking"
      :bookingStationTimesWithoutRemoved="bookingStationTimesWithoutRemoved"
      :conflict="bookingConflict"
      :startTime="startTime"
      :isOrganizationEducation="isOrganizationEducation"
      :saving="saving"
      :darkMode="darkMode"
      @toggleMarkAsPaid="toggleMarkAsPaid"
      @storeBooking="storeBooking" />
  </div>
</template>

<script>
import { computed, onBeforeUnmount } from '@vue/composition-api';
import { usePermissions } from '@/core/shared/setup/permissions';
import { useStore } from '@/core/store';

import ContentBlock from '@/core/shared/components/ContentBlock';
import ContentBlockGroup from '@/core/shared/components/ContentBlockGroup';
import UIButton from '@/core/shared/components/ui/UIButton';

import DateAndTime from './booking-station-time/date-and-time/DateAndTime';
import Experience from './booking-station-time/Experience';
import Stations from './booking-station-time/Stations';
import Promos from './booking-station-time/Promos';

import Tier from '@/location/booking/components/Tier';

import Players from './guest-list/Players';
import HostInformation from './guest-list/HostInformation';

import BottomOfBooking from './BottomOfBooking';
import SummaryGrid from './SummaryGrid';

import useBooking from '../compositions/useBooking';
import useBookingStationTimesWithoutRemoved from '../compositions/other/useBookingStationTimesWithoutRemoved';

export default {
  components: {
    ContentBlock,
    Experience,
    DateAndTime,
    Players,
    Stations,
    Tier,
    HostInformation,
    Promos,
    BottomOfBooking,
    SummaryGrid,
    UIButton,
    ContentBlockGroup,
  },
  props: {
    locationId: {
      type: String,
      required: true,
    },
    preSelected: {
      type: Object,
      required: false,
    },
    bookingId: {
      type: String,
      default: 'new',
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
  },
  setup ({ locationId, bookingId, preSelected }) {
    const permissions = usePermissions();
    const store = useStore();
    const isOrganizationEducation = computed(() => permissions.education);

    const {
      location,
      bookings,
      currentTime,
      booking,
      startTime,
      experiences,
      stations,
      getExperienceStations,
      getExperienceTiers,
      getExperienceCoupons,
      getExperienceDiscounts,

      bookingConflict,
      longestTierLength,
      editBooking,
      addBookingStationTime,
      removeBookingStationTime,
      undoRemoveBookingStationTime,
      setStartingTime,
      setStartingTimeNow,
      toggleMarkAsPaid,
      stationAction,
      tierAction,
      experienceAction,
      couponAction,
      discountAction,
      saving,
      isLoading,
      storeBooking,
      cancelBooking,
    } = useBooking(locationId, bookingId, preSelected);

    const {
      bookingStationTimesWithoutRemoved,
    } = useBookingStationTimesWithoutRemoved(booking);

    onBeforeUnmount(() => {
      store.dispatch('setLocationHeader');
    });

    return {
      isOrganizationEducation,

      location,
      bookings,
      currentTime,
      booking,
      bookingStationTimesWithoutRemoved,
      startTime,
      experiences,
      stations,
      getExperienceStations,
      getExperienceTiers,
      getExperienceCoupons,
      getExperienceDiscounts,

      bookingConflict,
      longestTierLength,
      editBooking,
      addBookingStationTime,
      removeBookingStationTime,
      undoRemoveBookingStationTime,
      setStartingTime,
      setStartingTimeNow,
      toggleMarkAsPaid,
      stationAction,
      tierAction,
      experienceAction,
      couponAction,
      discountAction,
      saving,
      isLoading,
      storeBooking,
      cancelBooking,
    };
  },
};
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.subheader {
  padding: var(--spacingMd) 0;
}

.cancelbooking {
  display: flex;
  align-items: center;
  color: var(--colorSBRedFlat);
  cursor: pointer;
  margin-left: auto;

  & svg {
    fill: var(--colorSBRedFlat);
    width: 2rem;
    height: 2rem;
    margin-right: var(--spacingXS);
  }
}

.bookingcontainer {
  @media (--desktop) {
    padding-bottom: 15rem;
  }

  & .experiencesettings {
    position: relative;
  }

  & .topofstationbookingtime {
    position: relative;

    & h2 {
      margin-top: 0;
      margin-bottom: var(--spacingLg);
    }
  }

  & .addstation {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: var(--spacingLg);

    @media (--desktop) {
      margin-left: auto;
      top: -var(--spacingMd);
      width: calc(100% - 38rem);
    }

    & .line {
      flex: 1 1 auto;
      height: 0.1rem;
      background-color: var(--colorGeyser);
    }
  }

  & .deletedstation {
    display: flex;
    align-items: center;

    & .undodeletestation {
      margin-left: auto;
    }
  }

  & button.remove {
    position: absolute;
    top: 0.5rem;
    right: 0;
    z-index: 2;
  }

  & .summary-grid {
    box-shadow: var(--shadowSm);
    padding: var(--spacingSm);
    margin-bottom: var(--spacingLg);

    @media (--tablet) {
      display: none;
    }
  }
}

.content-block.dark {
  background-color: var(--darkModeColorDarkBackground) !important;
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .promos label .text {
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .tiers .ui-button {
  background-color: var(--darkModeColorBackground);
  color: var(--darkModeColorText);
  & .label {
    color: var(--darkModeColorText) !important;
  }
}

.content-block.dark /deep/ .tiers .customtierinputs .ui-input {
  background-color: var(--darkModeColorBackground);
  color: var(--darkModeColorText);
}

.content-block.dark /deep/ .tiers .customtierinputs {
  & .customlength .label {
  color: var(--darkModeColorText);
  }
  & .customprice .label {
    color: var(--darkModeColorText);
  }
}

.content-block.dark /deep/ .stations .label {
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .stations .stationgrid .station .ui-button {
  background-color: var(--darkModeColorBackground);
  color: var(--darkModeColorText);
  & .label {
    color: var(--darkModeColorText) !important;
  }
}

.content-block.dark /deep/ .experiences .experiencegrid .experience .ui-button {
  background-color: var(--darkModeColorBackground);
  color: var(--darkModeColorText);
  & .label {
    color: var(--darkModeColorText) !important;
  }
}

.booking.dark .addstation .ui-button {
  background-color: var(--darkModeColorDarkBackground) !important;
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .range .label {
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .contact-info div .ui-input {
  background-color: var(--darkModeColorBackground);
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .contact-info .birthdate .birthdate-picker .formgroup .input .ui-select {
  background-color: var(--darkModeColorBackground);
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .contact-info .notes .ui-textarea {
  background-color: var(--darkModeColorBackground);
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .contact-info .notify  {
  & .switch:before {
    background-color: var(--darkModeColorLightBackground);
  }
  & .switch:after {
    background-color: var(--darkModeColorDarkBackground);
  }
  & .input:checked + .switch:before {
    background: var(--darkModeColorBackground);
    border: 1px var(--darkModeColorLightBackground) solid;
  }
}

</style>
